/* eslint-disable */
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { Sortable, SortableItem, SortableHandle } from '~/native-ui/sortable/Sortable';
import { arrayMove } from 'react-sortable-hoc';

import { Box, HStack, Center,  } from '~/native-ui/native-base';
import { Pressable } from '~/native-ui/Pressable';

import { Text, HighLightText } from '~/native-ui/Typography';
import { IconButton, Icon } from '~/native-ui/Icon';
import { Spinner } from '~/native-ui/Spinner';
import { FlatList } from '~/native-ui/List';
import { SpinnerInput as Input } from '~/native-ui/Spinner';
import { LinkToSpan } from '~/native-ui/Link';
import { renderHTML, extractNativeProps } from '~/native-ui/utils';
import { DropdownOption } from './DropdownOption';

/**
 * @typedef SourceRecordObject
 * @type {object}
 * @property {string} text - the label for the record.
 * @property {string | number} value - value of the record.
 * @property {boolean} [disabled=false] - is the record not selectable
 */

const MaybeSortableListItem = ({ orderable, index, ...props }) => {
    const key = `item-${index}`;
    return !orderable ? (
        props.children
    ) : (
        <SortableItem key={key} index={index}>
            <Box>{props.children}</Box>
        </SortableItem>
    );
};

const renderHtmlAsReact = (text, searchTerm) =>
    typeof text !== 'string'
        ? text
        : renderHTML(text, {
              a: LinkToSpan,
              p: (props) => props.children ?? null,
              Text: (props) => (
                  <Text pointerEvents='none'>
                      <HighLightText {...props}  pointerEvents='none' searchTerm={searchTerm} />
                  </Text>
              ),
          });

const CenteredSpinner = () => (
    <Center height="34px" width="34px">
        <Spinner size="xs" height="34px" />
    </Center>
);

const IconWrapper = (props) => {
    return <Center {...props} />;
};

const listItemFactory = (
    Input,
    parentProps,
    inputProps,
    { handleBlur, handleFocus, handleClick, renderIcons, renderTemplateValue }
) => {
    const { theme, template, labelKey, required, disabled, isReadOnly } = parentProps;
    return ({ value: selected, index }) => {
        const icons = renderIcons(selected, index, {
            ...(
                parentProps.multiSelect
                ? { iconAdd:  "add" }
                : { iconRemove: "clear" }
            ),
        });

        const textProps = {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        };

        const renderValues =
            template && selected ? (
                renderTemplateValue(selected, { disabled: disabled })
            ) : selected ? (
                <Text {...textProps}>{selected[labelKey]}</Text>
            ) : parentProps.placeholder ? (
                <Text {...textProps} color="muted.400">
                    {parentProps.placeholder}
                </Text>
            ) : null;

        return (
            <Pressable
                flexDirection="row"
                alignItems="center"
                onPress={disabled ? null : handleClick}
                onBlur={disabled ? null : handleBlur}
                backgroundColor={disabled ? '#f5f5f5' : undefined}
                cursor={disabled ? 'not-allowed' : undefined}
            >
                <Box paddingTop="2" paddingBottom="2" flex="1" {...textProps}>
                    {renderValues}
                </Box>
                {disabled || isReadOnly || (
                    <IconWrapper
                        ml="auto"
                        position="sticky"
                        right="0"
                        flexGrow={0}
                        flexShrink={0}
                        alignSelf="flex-start"
                    >
                        {icons}
                    </IconWrapper>
                )}
            </Pressable>
        );
    };
};

export default class SelectCore extends Component {
    static propTypes = {
        orderable: PropTypes.bool,
        multiSelect: PropTypes.bool,
        allowBlank: PropTypes.bool,
        allowFiltering: PropTypes.bool,
        allowClearing: PropTypes.bool,
        minQueryLength: PropTypes.number,
        auto: PropTypes.bool,
        className: PropTypes.string,
        disabled: PropTypes.bool,
        error: PropTypes.string,
        label: PropTypes.string,
        labelKey: PropTypes.string,
        name: PropTypes.string,
        onBlur: PropTypes.func,
        onChange: PropTypes.func,
        onClick: PropTypes.func,
        onFocus: PropTypes.func,
        onQueryChange: PropTypes.func,
        required: PropTypes.bool,
        source: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired,
        template: PropTypes.func,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
        valueKey: PropTypes.string,
    };

    static defaultProps = {
        auto: true,
        className: '',
        allowBlank: true,
        disabled: false,
        labelKey: 'label',
        required: false,
        valueKey: 'value',
        minCharacters: 0,
        multiSelect: false,
        orderable: false,
    };

    state = {
        active: false,
        up: false,
        minWidth: 0,
        query: '',
        source: [],
        focusedRecordIndex: -1,
    };

    controlledFilter = !!this.props.allowFiltering && !!this.props.onQueryChange;

    selectionsFlatListElem = React.createRef(null);
    optionsFlatListElem = React.createRef(null);
    filterInputElem = React.createRef(null);

    /**
     * @returns {SourceRecordObject[]}
     */
    get source() {
        return this.controlledFilter ? this.props.source : this.state.source;
    }

    get query() {
        return this.controlledFilter ? this.props.query : this.state.query;
    }

    get filteredSource() {
        return this.source.filter((item) => {
            return this.props.value.indexOf(item[this.props.valueKey]) < 0;
        });
    }
    
    componentDidMount() {
        if (!this.controlledFilter) this.onQueryChange('');
    }

    componentDidCatch(error, errorInfo) {
        // You can also log the error to an error reporting service
        console.log(error, errorInfo);
    }

    extractProps() {
        return extractNativeProps(this.props);
    }

    /**
     * 
     * @param {FlatList} node 
     */
    setOptionsFlatListRef = (node) => {
        if (!node) this.removeEventListeners();
        this.optionsFlatListElem.current = node;
        this.attachEventListeners();
    }


    getSelectedItem = () => {
        for (const item of this.props.source) {
            if (item[this.props.valueKey] === this.props.value) return item;
        }

        return !this.props.allowBlank ? this.props.source[0] : undefined;
    };

    getSelections = () => {
        let valueKey = this.props.valueKey;

        if (!this.props.multiSelect) {
            return [this.getSelectedItem()];
        }

        const itemsByKey = keyBy(this.props.source, valueKey);
        let items = this.props.value.map((value) => itemsByKey[value]).filter(Boolean);
        if (!items.length) {
            items = [undefined];
        }

        return items;
    };



    /**
     * 
     * @param {unknown} val 
     * @returns {unknown | unknown[]}
     */
    normalizeValueOnSelect (val) {
        if (this.props.multiSelect) {
            val = [...this.props.value, val].filter((v) => typeof v !== 'undefined');
        }

        return val;
    }    

    /**
     * 
     * @param {SourceRecordObject} val 
     * @param {MouseEvent | KeyboardEvent} event 
     * @returns 
     */
    handleSelect = (val, event) => {
        if (this.props.onBlur) this.props.onBlur(event);
        if (this.isDisabled() || !this.props.onChange) {
            return;
        }

        if (this.props.name) event.target.name = this.props.name;
        

        this.props.onChange(this.normalizeValueOnSelect(val), event);

        this.close('element selected');
    };

    handleClick = () => {
        if (this.props.disabled) return;
        const openFunc = (event) => {
            this.open(event);
        };
        if (!this.props.onClick) return openFunc;
        return (event) => {
            openFunc(event);
            event.preventDefault();
            this.props.onClick(event);
        };
    };

    close = (reason) => {
        if (!this.state.active) return;
        
        //console.log('dropdown closing because of ' + reason)
        this.setState({ active: false });
    };

    open = (event) => {
        if (this.state.active) return;
        const client = event.target.getBoundingClientRect();
        const screenHeight = window.innerHeight || document.documentElement.offsetHeight;
        const up = this.props.auto ? client.top > screenHeight / 2 + client.height : false;
        this.setState({ active: true, up, focusedRecordIndex: -1 });
    };

    attachEventListeners = () => {
        this.removeEventListeners();
        if (!this.optionsFlatListElem.current) return;
        window.addEventListener('keydown', this.onKeyboardEvent, true);
        this.optionsFlatListElem.current.getScrollableNode().addEventListener('mouseleave', this.handleMouseOut)
    }

    removeEventListeners () {
        window.removeEventListener('keydown', this.onKeyboardEvent, true);
        this.optionsFlatListElem.current?.getScrollableNode().removeEventListener('mouseleave', this.handleMouseOut)
    }

    componentWillUnmount() {
        this.removeEventListeners()
    }

    handleFocus = (event) => {
        event.preventDefault();
        if (!this.isDisabled()) this.open(event);
        this.props.onFocus?.(event);
    };

    handleBlur = (event) => {
        event.preventDefault();
        if (this.state.active) this.close('blur');
        this.props.onBlur?.(event);
    };

    renderTemplateValue = (selected, props) => {
        return <Pressable onPress={this.handleClick()}>{this.props.template(selected, props)}</Pressable>;
    }

    handleMouseOut = () => {
        this.setNextFocusedRecordIndex(-1);
    }

    onQueryChange = (query) => {
        if (this.controlledFilter) {
            this.props.onQueryChange(query);
            this.setState({
                focusedRecordIndex: -1,
            });
            return;
        }

        let { labelKey, source } = this.props,
            regex = new RegExp(query, 'ig');

        this.setState({
            query,
            source: source.filter((item) => regex.test(item[labelKey])),
            focusedRecordIndex: -1,
        });
    };

    setSearchFilterRef = (input) => {
        this.filterInputElem = input;
    };
    
    getIconLeft = () => {

        return (
            <Center height={10} width={10}>
                <Icon size="sm" type="search" iconFamily="material" />
            </Center>
        )
    }

    renderFilterInput = () => {
        const { loading } = this.props;

        return (
            <>
                <Input
                    loading={loading}
                    value={this.query}
                    ref={this.setSearchFilterRef}
                    px="3"
                    pl="0"
                    iconLeft={this.getIconLeft()}
                    placeholder={get(this.props.query, 'length', 0) ? '' : this.props.filterHint}
                    onChange={this.onQueryChange}
                />
            </>
        );
    };

    unselectItem = (item) => {
        if (this.props.disabled) return;
        
        let value = null;

        if (this.props.multiSelect) {
            value = this.props.value.filter((i) => i !== item?.value);
        }

        this.props.onChange(value);
    };

    isValueEmpty() {
        let { value, multiSelect } = this.props;

        if (multiSelect) {
            return value.filter((v) => typeof v !== 'undefined').length < 1;
        }

        return value === null || value === '';
    }

    /**
     * @typedef IconConfig
     * @type {object}
     * @property {string} [iconNotEmpty]
     * @property {string} [iconRemove]
     * @property {string} [iconDragIndicator]
     * @property {isLoading} [isLoading]
     * @property {object} [iconProps]
     * @property {object} [wrapperProps]

     */

    /**
     * 
     * @param {SourceRecordObject} item 
     * @param {number} i 
     * @param {IconConfig} iconConfiguration
     * @returns 
     */
    renderIcons = (item, i, {
        iconNotEmpty = 'keyboard-arrow-down',
        iconRemove = 'remove',
        iconAdd = iconNotEmpty,
        iconDragIndicator = 'drag-indicator',
        isLoading = this.props.loading,
        iconProps = {},
        wrapperProps = {},
    } = {}) => {
        let icons = [];

        if (isLoading && i === 0) {
            icons.push(<CenteredSpinner key="spinner" />);
            return icons;
        }

        if (this.props.disabled) {
            if (!icons.length) {
                icons.push(
                    <IconButton
                        colorScheme="muted"
                        pointerEvents="none"
                        p="5px"
                        opacity="0"
                        rounded="0"
                        roundedTop="xs"
                        type={iconRemove}
                        key="clear"
                        {...iconProps}
                    />
                );
            }
            return <HStack { ...wrapperProps }>{icons}</HStack>;
        }

        if (this.isValueEmpty() || !this.props.allowBlank) {
            icons.push(
                <IconButton
                    colorScheme="muted"
                    p="5px"
                    rounded="0"
                    roundedTop="xs"
                    type={iconAdd || iconNotEmpty}
                    key="open"
                    onClick={this.handleClick()}
                    {...iconProps}
                />
            );
        } else if (this.props.allowBlank) {
            icons.push(
                <IconButton
                    colorScheme="muted"
                    p="5px"
                    rounded="0"
                    roundedTop="xs"
                    type={iconRemove}
                    key="clear"
                    onClick={() => this.unselectItem(item)}
                    {...iconProps}
                />
            );
        }

        if (this.props.orderable && this.getSelections().length > 1) {
            icons.push(
                <SortableHandle>
                    <Icon
                        colorScheme="muted"
                        p="5px"
                        rounded="0"
                        roundedTop="xs"
                        type={iconDragIndicator}
                        key="drag"
                        className="dragHandle"
                        {...iconProps}
                    />
                </SortableHandle>
            );
        }

        return <HStack { ...wrapperProps }>{icons}</HStack>;
    }

    /**
     * 
     * @param {KeyboardEvent} e 
     */
    onKeyboardEvent = (e) => {
        const keyCode = e.code;
        if (!["ArrowDown", "ArrowUp", "Enter"].includes(keyCode)) return;

        const {valueKey} = this.props;
        const focusedRecordIndex = this.state.focusedRecordIndex;
        let nextFocusedRecordIndex = focusedRecordIndex;
        let source = this.source;

        if (this.props.multiSelect) {
            source = this.filteredSource;
        } else if (nextFocusedRecordIndex < 0) {
            const firstSelectedItem = this.getSelections()[0];
            nextFocusedRecordIndex = !firstSelectedItem ? 0 : source.indexOf((record) => firstSelectedItem[valueKey] === record[valueKey])
        }

        if (!source.some((record) => !record.disabled)) {
            this.setNextFocusedRecordIndex(-1);
            return;
        };

        e.stopImmediatePropagation();


        if (keyCode === 'Enter') {
            this.handleSelect(source[focusedRecordIndex][valueKey], e)
            return;
        }

        
        
        const offset = keyCode === 'ArrowDown' ? 1 : -1;
        
        nextFocusedRecordIndex = Math.max(focusedRecordIndex, -1);
        while(true) {
            nextFocusedRecordIndex += offset;


            if (source[nextFocusedRecordIndex] && !source[nextFocusedRecordIndex].disabled) {
                break;
            }

            if (nextFocusedRecordIndex < 0) {
                nextFocusedRecordIndex = source.length - 1;
                break;
            }

            if (nextFocusedRecordIndex > source.length - 1) {
                nextFocusedRecordIndex = 0;
                break;
            }

        }

        this.setNextFocusedRecordIndex(nextFocusedRecordIndex);
    }

    /**
     * 
     * @param {number} nextFocusedRecordIndex 
     */
    setNextFocusedRecordIndex = (nextFocusedRecordIndex) => {
        this.setState({ focusedRecordIndex: nextFocusedRecordIndex });
        if (nextFocusedRecordIndex < 0 || !this.optionsFlatListElem.current) return;
        this.optionsFlatListElem.current.scrollToIndex({ index: nextFocusedRecordIndex, animated: false })
    }

    /**
     * 
     * 
     * @param {SourceRecordObject} value 
     * @param {number} index 
     * @param {string} [query] 
     * @returns {import('react').ReactNode}
     * 
     */
    renderOptionLabel (value, _index, query) {
        return renderHtmlAsReact(value[this.props.labelKey], query)
    }


    renderOptionIcons() {
        return null;
    }



    /**
     * 
     * 
     * @param {SourceRecordObject} value 
     * @param {number} index 
     * @param {object} initialProps
     * @returns {import('react').ReactNode}
     * 
     */
    getOptionProps (_value, _index, initialProps) {
        return initialProps
    }

    renderOption (item, index, query) {
        return (hoverState) => {
            const label = this.renderOptionLabel(item, index, query);
            const icons = this.renderOptionIcons(item, index, query);

            return (
                <>
                    {typeof label === 'function' ? label(hoverState) : label}
                    {typeof icons === 'function' ? icons(hoverState) : icons}
                </>
            )
        }
    }

    
    renderOptions() {
        let { source, query, props } = this,
            { valueKey, labelKey, multiSelect, minQueryLength, allowFiltering } = props;

        if (allowFiltering && minQueryLength) {
            if (query.length < minQueryLength) {
                return [
                    {
                        [labelKey]: `Please enter at least ${minQueryLength - query.length} more characters.`,
                        [valueKey]: 'system_minimum_chars',
                        disabled: true,
                    },
                ];
            }
        }

        if (multiSelect) {
            source = this.filteredSource;
        }

        
        return (
            <FlatList
                data={source}
                ref={this.setOptionsFlatListRef}
                initialNumToRender={this.props.initialNumToRender}
                disableVirtualization={this.props.disableVirtualization}
                maxHeight='50vh'
                renderItem={({ item, index }) => {
                    const optionProps = this.getOptionProps(item, index, {
                        disabled: item.disabled,
                        borderTopWidth: index === 0 ? 0 : 1,
                        onClick: (e) => this.handleSelect(item[valueKey], e),
                        bg: this.state.focusedRecordIndex === index ? 'coolGray.200' : undefined,
                    })

                    return (
                        <DropdownOption {...optionProps}>
                            {this.renderOption(item, index, query)}
                        </DropdownOption>
                    );
                }}
                keyExtractor={(item, index) => {
                    return item?.[valueKey] || index;
                }}
            />
        );
    }

    /**
     * 
        * @typedef ActionsObject
        * @type {object}
        * @property { (e: FocusEvent<HTMLInputElement>) => void } [handleBlur]
        * @property { (e: FocusEvent<HTMLInputElement>) => void } [handleFocus]
        * @property { (e: ClickEvent<HTMLInputElement>) => void } [handleClick]

     * 
     * @param {*} inputProps 
     * @param { ActionsObject } [actions={}]
     */
    getListItemElement(inputProps, actions = {}) {
        return listItemFactory(Input, this.props, inputProps, {
            handleBlur: this.handleBlur,
            handleFocus: this.handleFocus,
            renderIcons: this.renderIcons,
            renderTemplateValue: this.renderTemplateValue,
            ...actions
        });
    }

    renderSelectedItem = (ListItem, orderable, selected, i) => {
        return (
            <MaybeSortableListItem key={`item-${i}`} index={i} orderable={orderable}>
                <ListItem value={selected} index={i} />
            </MaybeSortableListItem>
        );
    };



    handleReorderItems = (oldIndex, newIndex) => {
        let valueKey = this.props.valueKey;
        const values = arrayMove(this.getSelections().filter(Boolean), oldIndex, newIndex).map((item) => get(item, valueKey));
        this.props.onChange(values);
    };


    selectedItemsListNode = React.createRef(null);
    setSelectedItemsListNode = (node) => {
        this.selectedItemsListNode = node;
    };

    getDropdownDragContainer = () => {
        return this.selectedItemsListNode;
    };

    setWidth = (event) => {
        const { width } = event.nativeEvent.layout;
        this.setState({ maxWidth: width });
    };

    focusStyle = { borderBottomWidth: 2, borderBottomColor: 'primary.500' };
    renderSelections(triggerProps, inputProps) {
        let selectedItems = this.getSelections();
        let { valueKey, orderable } = this.props;

        orderable = orderable && selectedItems.length > 1;
        const listItem = this.getListItemElement(inputProps);

        
        const list = (
            <Box {...triggerProps} borderBottomWidth={1} borderBottomColor="gray.300" _focus={this.focusStyle}>
                <FlatList
                    onLayout={this.setWidth}
                    ref={this.selectionsFlatListElem}
                    data={selectedItems}
                    renderItem={({ item, index }) => this.renderSelectedItem(listItem, orderable, item, index)}
                    keyExtractor={(item, index) => {
                        return item?.[valueKey] || index;
                    }}
                />
            </Box>
        );

        if (!(orderable && selectedItems.length > 1)) return list;

        return (
            <Sortable
                useDragHandle
                getContainer={this.getDropdownDragContainer}
                onSortEnd={this.handleReorderItems.bind(this)}
                axis="y"
            >
                <Box ref={this.setSelectedItemsListNode}>{list}</Box>
            </Sortable>
        );
    }

    isDisabled = () => {
        return this.props.loading || this.props.disabled;
    };

    renderDropdownInterface = (props) => {
        const {
            allowBlank,
            auto,
            labelKey,
            required,
            onChange,
            onFocus,
            onBlur, // eslint-disable-line no-unused-vars
            source,
            template,
            minCharacters,
            multiSelect,
            minQueryLength,
            loading,
            filterHint,
            allowFiltering,
            onQueryChange,
            query,
            small,
            valueKey,
            optionClassName,
            label,
            tooltip,
            error,
            disabled,
            ...others
        } = this.props;

        return this.renderSelections(props, { disabled, ...others });
    };

    
    render() {
        return null;
    }
}